import { Formik } from 'formik';
import React, { useCallback, useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { useAddKeyInformationMutation, useUploadImageMutation } from '../drApi/drApi';
import TitleBar from '../detailsPageComponents/TitleBar';
import CardsContainer from '../cards/CardsContainer';
import Card from '../cards/Card';
import { KEY_TYPES } from '../drApi/constants/keyType';
import { KEY_POSITION_TYPES } from '../drApi/constants/keyPositionType';
import CardTextArea from '../EditableCard/CardTextArea';
import StopListField from './fields/StopListField';
import CardSectionTitle from '../detailsPageComponents/CardSectionTitle';
import KeyInstanceField from './fields/KeyInstanceField';
import styles from './KeyInformation.module.scss';
import KeyTypesField from './fields/KeyTypesField';
import KeyIdBadge from '../KeyIdBadge/KeyIdBadge';
import { getGloballySelectedHouseId } from '../drApi/housesSelectors';
import { errorToast } from '../toasts/toastsThunks';
import KeyImagePicker from './KeyImagePicker';
import pickerStyles from './KeyImagePicker.module.scss';

const NewKeyInformation = () => {
  const { goBack, push: historyPush } = useHistory();
  const dispatch = useDispatch();
  const houseId = useSelector(getGloballySelectedHouseId);
  const { t } = useTranslation();
  const [selectedImage, setSelectedImage] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [isSaving, setIsSaving] = useState(false);

  const [addKeyInformation, { isLoading }] = useAddKeyInformationMutation();
  const [uploadImage] = useUploadImageMutation();

  useEffect(() => {
    return () => {
      if (previewUrl) {
        URL.revokeObjectURL(previewUrl);
      }
    };
  }, [previewUrl]);

  const onSubmit = useCallback(
    async (values) => {
      if (isSaving) {
        return;
      }

      setIsSaving(true);
      const { note, stops, keyInstance, keyTypes } = values;

      let image = null;
      try {
        if (selectedImage) {
          try {
            const response = await uploadImage(selectedImage);
            const { data, error } = response || {};
            if (error || !data) {
              dispatch(errorToast({ text: t('Upload fehlgeschlagen') + (error?.message ? `: ${error.message}` : '') }));
              return;
            }
            image = data?.imageUrl;
          } catch {
            image = false;
          }
          if (!image) {
            dispatch(errorToast({ text: t('Upload fehlgeschlagen') }));
            return;
          }
        }

        const { keyInformationId } = await addKeyInformation({
          body: {
            newKeyInformation: {
              houseId,
              keyTypes,
              image,
              note,
              // weird format for api...
              stopIds: stops
                .filter((s) => !!s)
                .reduce((red, stop, index) => {
                  return { ...red, [index]: { newStop: stop._id } };
                }, {}),
            },
            newKeyInstances: [keyInstance],
          },
        }).unwrap();
        historyPush(`/keys/${keyInformationId}`);
      } finally {
        setIsSaving(false);
      }
    },
    [addKeyInformation, dispatch, uploadImage, historyPush, houseId, isSaving, selectedImage, t],
  );

  const onCancel = useCallback(() => {
    goBack();
  }, [goBack]);

  const onFileSelected = useCallback((event) => {
    const file = event.currentTarget.files?.[0];
    if (!file) {
      return;
    }

    if (previewUrl) {
      URL.revokeObjectURL(previewUrl);
    }

    setSelectedImage(file);
    setPreviewUrl(URL.createObjectURL(file));
  }, [previewUrl]);

  const onRemoveImage = useCallback(() => {
    if (previewUrl) {
      URL.revokeObjectURL(previewUrl);
    }
    setSelectedImage(null);
    setPreviewUrl(null);
  }, [previewUrl]);

  return (
    <Formik
      initialValues={{
        note: '',
        stops: [],
        keyInstance: {
          name: '',
          currentPosition: KEY_POSITION_TYPES.CABINET,
          currentUser: null,
        },
        keyTypes: {
          [KEY_TYPES.KEY]: 0,
          [KEY_TYPES.CARD]: 0,
          [KEY_TYPES.REMOTE]: 0,
        },
      }}
      onSubmit={onSubmit}
    >
      {({
        handleSubmit,
        values: {
          keyInstance: { name },
        },
      }) => (
        <>
          <TitleBar
            title={<KeyIdBadge name={name || ''} className={!name ? styles.hidden : ''} />}
            isLoading={isLoading || isSaving}
            onAccept={handleSubmit}
            onCancel={onCancel}
            acceptLabel="Schlüssel Anlegen"
          />
          <CardsContainer>
            <Card title={t('Bemerkung')} large>
              <CardTextArea name="note" disabled={isLoading || isSaving} />
            </Card>
            <Card title={t('Stopadressen')} large>
              <StopListField name="stops" isEditing disabled={isLoading || isSaving} />
            </Card>
            <Card title={t('Informationen')} large>
              <KeyTypesField name="keyTypes" isEditing disabled={isLoading || isSaving} />
            </Card>
            <Card title={t('Bild')} large className={pickerStyles.card}>
              <KeyImagePicker
                imageUrl={previewUrl}
                onFileSelected={onFileSelected}
                onRemove={onRemoveImage}
                isEditing
                disabled={isLoading || isSaving}
              />
            </Card>
          </CardsContainer>
          <CardsContainer>
            <CardsContainer vertical>
              <CardSectionTitle title={t('Schlüssel-ID')} />
              <Card autoHeight>
                <KeyInstanceField isEditing namePrefix="keyInstance." disabled={isLoading || isSaving} />
              </Card>
            </CardsContainer>
            <CardsContainer vertical>
              <CardSectionTitle title={t('Weitere Schlüssel IDs')} />
              <div className={styles.addMoreKeys}>
                {t('Weitere Schlüssel IDs können im nächsten Schritt hinzugefügt werden')}
              </div>
            </CardsContainer>
          </CardsContainer>
        </>
      )}
    </Formik>
  );
};

export default NewKeyInformation;
